/**
 * Narrowing the traders list — ROADMAP 9.12. Pure, therefore tested.
 *
 * On a deployment with four logins a filter is decoration. The reason it is
 * here anyway is the one case it is not: finding the one account whose
 * slicing somebody switched on, in a list where every row looks alike,
 * during an incident. That is the search this screen exists to serve, so it
 * matches on what an admin would actually type — an address, a name, or the
 * name of a capability.
 */

import {
  capabilityOf,
  EXECUTION_CAPABILITIES,
  CAPABILITY_LABEL,
  type ExecutionCapability,
  type TraderPolicyDto,
} from '@apna/shared'

export interface TraderFilter {
  /** Free text: email, name, role, or a capability's label. */
  readonly query: string
  /** Only traders who could actually place an order. */
  readonly withAccountsOnly: boolean
  /** Only traders who have this capability ON. Null means no restriction. */
  readonly capability: ExecutionCapability | null
}

export const NO_FILTER: TraderFilter = { query: '', withAccountsOnly: false, capability: null }

/**
 * Does the typed text match this row?
 *
 * Capability LABELS are searchable, so typing "netting" finds the rows that
 * have it on rather than nothing. It matches only when the capability is ON:
 * a search for a capability is a search for who has it, and returning every
 * trader because they all have the word in their row would be useless.
 */
function matchesQuery(trader: TraderPolicyDto, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (q === '') return true

  const haystack = [trader.email, trader.name ?? '', trader.role]
  for (const capability of EXECUTION_CAPABILITIES) {
    if (capabilityOf(trader.policy, capability)) haystack.push(CAPABILITY_LABEL[capability])
  }
  return haystack.some((field) => field.toLowerCase().includes(q))
}

export function filterTraders(
  traders: readonly TraderPolicyDto[],
  filter: TraderFilter,
): TraderPolicyDto[] {
  return traders.filter((t) => {
    if (filter.withAccountsOnly && t.accounts === 0) return false
    if (filter.capability !== null && !capabilityOf(t.policy, filter.capability)) return false
    return matchesQuery(t, filter.query)
  })
}

/** How many traders have each capability on. For the header summary. */
export function capabilityCounts(
  traders: readonly TraderPolicyDto[],
): Record<ExecutionCapability, number> {
  const out = { sliceEnabled: 0, autoChaseEnabled: 0, manualNetting: 0 }
  for (const t of traders) {
    for (const capability of EXECUTION_CAPABILITIES) {
      if (capabilityOf(t.policy, capability)) out[capability] += 1
    }
  }
  return out
}

/**
 * The sentence a bulk confirmation shows.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT NAMES THE LIVE BOOKS, AND THAT IS THE POINT OF THE DIALOG
 *
 * One toggle is one visible row and one click to undo. A bulk action is N
 * people's order path at once, and a mis-click on it is not visible per row —
 * which is why it asks first.
 *
 * What it has to say is not "are you sure" but WHAT IS DIFFERENT about doing
 * it now: a trader holding open positions has their next click change meaning
 * mid-book. Granting netting to someone flat is housekeeping; granting it to
 * someone with eleven legs on the screen is a change they will feel on their
 * next sell.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function bulkWarning(
  selected: readonly TraderPolicyDto[],
  capability: ExecutionCapability,
  on: boolean,
): string {
  const label = CAPABILITY_LABEL[capability].toLowerCase()
  const live = selected.filter((t) => t.openTrades > 0)
  const head = `${on ? 'Switching on' : 'Switching off'} ${label} for ${selected.length} ${selected.length === 1 ? 'login' : 'logins'}.`

  if (live.length === 0) {
    return `${head} None of them is holding an open position, so nothing changes until their next order.`
  }
  const names = live.slice(0, 3).map((t) => t.email).join(', ')
  const more = live.length > 3 ? ` and ${live.length - 3} more` : ''
  return (
    `${head} ${live.length} of them ${live.length === 1 ? 'is' : 'are'} holding open positions right now ` +
    `(${names}${more}) — this changes what their next order does, mid-book.`
  )
}
